import { useSession } from '@zhiyue/core';
import React from 'react';
import {
  readCurrentParkUuid,
  readCurrentTenantUuid,
  resolveCurrentParkUuid,
  saveCurrentParkUuid,
} from '@/adapters/tenantStorage';
import { useEnterpriseBootstrap } from '@/enterprise/bootstrap';
import { getActiveMembershipParks } from '@/park/scope';
import type { Park } from '@/services/bff/types/park';

type ParkContextValue = {
  tenantUuid?: string;
  parks: Park[];
  parkUuid?: string;
  currentPark?: Park;
  hasActivePark: boolean;
  loading: boolean;
  setParkUuid: (parkUuid?: string) => void;
};

const ParkContext = React.createContext<ParkContextValue | undefined>(
  undefined,
);

export const resolveBoundParkContext = ({
  tenantUuid,
  bootstrapTenantUuid,
  bootstrapLoading,
  parks,
  storedParkUuid,
  preferredParkUuid,
}: {
  tenantUuid?: string | null;
  bootstrapTenantUuid?: string;
  bootstrapLoading: boolean;
  parks: Park[];
  storedParkUuid?: string | null;
  preferredParkUuid?: string | null;
}) => {
  const tenantMatched =
    Boolean(tenantUuid) && tenantUuid === bootstrapTenantUuid;
  if (!tenantMatched || (bootstrapLoading && parks.length === 0)) {
    return {
      parks: [] as Park[],
      parkUuid: undefined as string | undefined,
      loading: true,
    };
  }
  return {
    parks,
    parkUuid: resolveCurrentParkUuid(parks, storedParkUuid, preferredParkUuid),
    loading: false,
  };
};

export const ParkContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { currentUser } = useSession();
  const {
    data: bootstrap,
    loading: bootstrapLoading,
    tenantUuid: bootstrapTenantUuid,
    refresh,
  } = useEnterpriseBootstrap();
  const tenantUuid =
    currentUser?.tenantUuid || readCurrentTenantUuid() || undefined;

  React.useEffect(() => {
    const handleAccessibleParksChanged = () => {
      void refresh();
    };
    window.addEventListener(
      'accessible-parks-changed',
      handleAccessibleParksChanged,
    );
    return () =>
      window.removeEventListener(
        'accessible-parks-changed',
        handleAccessibleParksChanged,
      );
  }, [refresh]);

  const membershipParks = React.useMemo(
    () =>
      getActiveMembershipParks<Park>(
        bootstrap?.parks,
        bootstrap?.accessibleParks,
        bootstrap?.state,
      ),
    [bootstrap?.accessibleParks, bootstrap?.parks, bootstrap?.state],
  );

  const preferredParkUuid =
    bootstrap?.relation?.status === 'active'
      ? bootstrap.currentPark?.uuid
      : undefined;

  const bound = resolveBoundParkContext({
    tenantUuid,
    bootstrapTenantUuid,
    bootstrapLoading,
    parks: membershipParks,
    storedParkUuid: readCurrentParkUuid(tenantUuid),
    preferredParkUuid,
  });

  const [parkUuid, setParkUuidState] = React.useState<string | undefined>();

  const applyParkUuid = React.useCallback(
    (resolved?: string, boundTenantUuid?: string) => {
      if (boundTenantUuid && boundTenantUuid !== tenantUuid) {
        return;
      }
      const stored = readCurrentParkUuid(boundTenantUuid) || undefined;
      setParkUuidState(resolved);
      if (resolved !== stored) {
        saveCurrentParkUuid(resolved, boundTenantUuid);
        window.dispatchEvent(new Event('park-context-changed'));
      }
    },
    [tenantUuid],
  );

  React.useLayoutEffect(() => {
    if (bound.loading) {
      setParkUuidState(undefined);
      return;
    }
    applyParkUuid(bound.parkUuid, tenantUuid);
  }, [
    applyParkUuid,
    bound.loading,
    bound.parkUuid,
    bootstrapTenantUuid,
    tenantUuid,
  ]);

  const setParkUuid = React.useCallback(
    (next?: string) => {
      if (bound.loading) return;
      const resolved =
        next && bound.parks.some((park) => park.uuid === next)
          ? next
          : resolveCurrentParkUuid(bound.parks, next, preferredParkUuid);
      applyParkUuid(resolved, tenantUuid);
    },
    [applyParkUuid, bound.loading, bound.parks, preferredParkUuid, tenantUuid],
  );

  const resolvedParkUuid = bound.loading
    ? undefined
    : parkUuid && bound.parks.some((park) => park.uuid === parkUuid)
      ? parkUuid
      : bound.parkUuid;
  const currentPark = bound.parks.find(
    (park) => park.uuid === resolvedParkUuid,
  );
  const value = React.useMemo<ParkContextValue>(
    () => ({
      tenantUuid,
      parks: bound.parks,
      parkUuid: resolvedParkUuid,
      currentPark: bound.loading ? undefined : currentPark,
      hasActivePark: Boolean(!bound.loading && resolvedParkUuid),
      loading: bound.loading,
      setParkUuid,
    }),
    [
      bound.loading,
      bound.parks,
      currentPark,
      resolvedParkUuid,
      setParkUuid,
      tenantUuid,
    ],
  );

  return <ParkContext.Provider value={value}>{children}</ParkContext.Provider>;
};

export const useParkContext = () => {
  const value = React.useContext(ParkContext);
  if (!value) {
    throw new Error('useParkContext must be used within ParkContextProvider');
  }
  return value;
};
